import type {
  RecommendationCardResponse,
  RecommendationCardsResponse,
  SubmitCardFeedbackRequest,
  SubmitCardFeedbackResponse,
} from "@/api/insights";

import { PREVIEW_PASSTHROUGH, type PreviewPassthrough } from "../registry";

import {
  HCIC_DEMO_PROD_ENV_ID,
  getCurrentEnvId,
  isCurrentEnvHcicDemo,
} from "../hcic-demo";

// Canned recommendation cards for the HCIC booth demo. These mirror the three
// cards Kylie's "improve my paid marketing" chat answer references, so the
// chat's "View Recommendation Cards" link lands on the matching /attribution
// cards. Scoped to the HCIC env — every other env keeps the real backend.

const GENERATED_AT = "2026-10-13T09:00:00.000Z";
const DATE_RANGE_END = "2026-10-12T23:59:59.000Z";
const CURRENT_WINDOW = {
  begin: "2026-09-13T00:00:00.000Z",
  end: DATE_RANGE_END,
};

function hcicCards(envId: string): RecommendationCardResponse[] {
  const base = {
    envId,
    accountId: "preview-hcic-account",
    generatedAt: GENERATED_AT,
    dateRangeEnd: DATE_RANGE_END,
    timeWindowUsed: "30d" as const,
    attributionModelUsed: "last_touch" as const,
    attributionModelReason:
      "Last touch credits the final click before each conversion, the clearest view of which channel closed each appointment request.",
    dataQualityLabel: "High" as const,
    dataQualityReason:
      "Spend and conversions are syncing for every paid channel this week.",
    changeStatus: "new" as const,
    changeNote: "New this week.",
    bottomFunnelEvents: ["Appointment Request Submitted"],
    midFunnelEvents: [],
  };

  return [
    {
      ...base,
      cardId: "hcic-card-google-cpa",
      cardRank: 1,
      category: "Channel Performance",
      title: "Find out why Google Ads CPA is climbing",
      teaser:
        "Google Ads CPA rose 29% to $141 while conversions fell about 25%.",
      shortRecommendation:
        "Google Ads CPA rose 29% to $141 while conversions fell about 25%. Check which campaigns and landing pages drove the change before you add budget.",
      fullRecommendation:
        "Google Ads is still your top paid channel, with 113 conversions (70% of paid volume) in the last 30 days. Its cost per acquisition (CPA), though, rose to $141, up 29% from $109 over the previous 60 days, and conversions fell about 25%. Check which campaigns and landing pages drove the change before you add budget.",
      supportingMetrics: [
        {
          dateRange: CURRENT_WINDOW,
          definition: "Google Ads cost per acquisition",
          metric: "cpa",
          value: "$141",
          dataSources: ["ad_spend", "bottom_funnel_events"],
        },
        {
          dateRange: CURRENT_WINDOW,
          definition: "Google Ads bottom-funnel conversions",
          metric: "conversions",
          value: "113",
          dataSources: ["bottom_funnel_events"],
        },
      ],
    },
    {
      ...base,
      cardId: "hcic-card-bing-budget",
      cardRank: 2,
      category: "Budget",
      title: "Test more budget on Bing Ads",
      teaser: "Bing Ads has your lowest CPA ($86) but only ~6% of paid spend.",
      shortRecommendation:
        "Bing Ads has your lowest CPA at $86 but gets only about 6% of your paid spend. A small shift from Google Ads could add conversions at a lower cost.",
      fullRecommendation:
        "Bing Ads has your lowest CPA at $86, and it's held steady for 90 days. It gets only about 6% of your paid spend ($1,550 of $27,850 in the last 30 days). A small shift from Google Ads could add conversions at a lower cost.",
      supportingMetrics: [
        {
          dateRange: CURRENT_WINDOW,
          definition: "Bing Ads cost per acquisition",
          metric: "cpa",
          value: "$86",
          dataSources: ["ad_spend", "bottom_funnel_events"],
        },
        {
          dateRange: CURRENT_WINDOW,
          definition: "Bing Ads share of paid spend",
          metric: "spend_share",
          value: "6%",
          dataSources: ["ad_spend"],
        },
      ],
    },
    {
      ...base,
      cardId: "hcic-card-meta-role",
      cardRank: 3,
      category: "Channel Role",
      title: "Don't cut Meta Ads based on last touch",
      teaser:
        "Meta's $270 last-touch CPA hides its role: it opens 64% of the paths it appears in.",
      shortRecommendation:
        "Meta's $270 last-touch CPA hides its role as an opener. Under first touch its CPA is about $92. Compare models before making budget decisions.",
      fullRecommendation:
        "Meta Ads looks like your most expensive channel, with a $270 CPA under last touch. But Meta opens 64% of the conversion paths it appears in, and last touch doesn't credit that. Under first touch, Meta gets 79 conversions, for a CPA of about $92. Compare models before you make budget decisions for Meta.",
      supportingMetrics: [
        {
          dateRange: CURRENT_WINDOW,
          definition: "Meta Ads CPA under last touch",
          metric: "cpa_last_touch",
          value: "$270",
          dataSources: ["ad_spend", "bottom_funnel_events"],
        },
        {
          dateRange: CURRENT_WINDOW,
          definition: "Meta Ads CPA under first touch",
          metric: "cpa_first_touch",
          value: "$92",
          dataSources: ["ad_spend", "bottom_funnel_events"],
        },
      ],
    },
  ];
}

export function handleGetRecommendationCards(
  _params: Record<string, never>,
): RecommendationCardsResponse | PreviewPassthrough {
  // Only the HCIC booth demo has canned cards. Every other env passes through
  // to the real backend, so the sales engineering team running preview mode in
  // their own env still sees their real recommendation cards instead of a blank
  // list.
  if (!isCurrentEnvHcicDemo()) {
    return PREVIEW_PASSTHROUGH;
  }
  const envId = getCurrentEnvId() ?? HCIC_DEMO_PROD_ENV_ID;
  return {
    envId,
    state: "ok",
    cards: hcicCards(envId),
    updatedAt: GENERATED_AT,
  };
}

export function handleSubmitRecommendationCardFeedback(
  params: SubmitCardFeedbackRequest,
): SubmitCardFeedbackResponse {
  return {
    feedback: { sentiment: params.sentiment, hasComment: !!params.comment },
  };
}
